import classNames from 'classnames';
import { useState, type CSSProperties, type ReactNode } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled, { keyframes } from 'styled-components';

import { fadeIn } from '@sorare/core/src/atoms/animations/keyframes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandL, BrandXXL, LabelL } from '@sorare/core/src/atoms/typography';
import { RewardImageShine } from '@sorare/core/src/components/rewards/RewardUnlockedAnimation/index.tsx';
import { useSeriesNav } from '@sorare/core/src/hooks/commonGame/useSeriesNav.ts';
import { glossary } from '@sorare/core/src/lib/glossary';
import { SetColorTheme } from '@sorare/core/src/set/SetColorTheme/index.tsx';
import { useCommonGameSet } from '@sorare/core/src/set/index.ts';

import { ButtonsWrapper, Root } from '../styles.tsx';

export type RewardGroupDisplayProps = {
  title: ReactNode;
  subtitle?: ReactNode;
  amount?: ReactNode;
  action?: ReactNode;
  rgbColor?: string;
  children?: ReactNode;
  disclaimer?: ReactNode;
  imgUrls: string[];
};

const StyledRoot = styled(Root)`
  --image-size: calc(22 * var(--unit));
`;
const imageEntrance = keyframes`
  from {
    transform:  translateX(50%) scale(1.67) ;
    opacity: 0;
  }
  to {
    transform:  translateX(50%) scale(1) ;
    opacity: 1;
  }
`;

const firstImageAnimation = keyframes`
0% {
  transform:  translateX(50%) scale(1) ;
  opacity: 1;
}
  20% {
    transform:   translateX(50%) scale(0.7);
    opacity: 1;
  }
  100% {
    transform:  translateX(0)scale(0.7) ;
    opacity: 1;
  }
`;

const Images = styled(Horizontal).attrs({ center: true })`
  & > * {
    flex: 1;
  }
`;

const SecondImageWrapper = styled(Vertical).attrs({ center: true })`
  width: calc(var(--image-size) * 0.7);
  height: calc(var(--image-size) * 0.7);
  transition: all 0.5s 0.3s var(--ease-in-out-quart);
`;

const EntranceImageWrapper = styled(Vertical).attrs({ center: true })`
  position: relative;
  width: var(--image-size);
  height: var(--image-size);
  animation: ${imageEntrance} 0.6s var(--ease-in-out-quart) forwards;
  animation-delay: 0.5s;
  opacity: 0;
  &.animate {
    animation: ${firstImageAnimation} 0.6s var(--ease-in-out-quart) forwards;
  }
`;

const TransitionRewardImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const TransitionTextArea = styled(Vertical).attrs({ gap: 2, center: true })`
  opacity: 0;
  animation: ${fadeIn} 0.65s var(--ease-in-out-quart) forwards;
  animation-delay: 0.55s;
`;

const messages = defineMessages({
  andMore: {
    defaultMessage: 'And...',
  },
});

type DualRewardUnlockedStoryProps = {
  header?: ReactNode;
  onClose: () => void;
  group1Props: RewardGroupDisplayProps;
  group2Props: RewardGroupDisplayProps;
  showContinueButton?: boolean;
  continueButtonColor?: 'primary' | 'secondary';
  hideBackground?: boolean;
  useSetBackground?: boolean;
};

export const DualRewardUnlockedStory = ({
  header,
  onClose,
  group1Props,
  group2Props,
  showContinueButton = true,
  continueButtonColor = 'primary',
  hideBackground,
  useSetBackground,
}: DualRewardUnlockedStoryProps) => {
  const isSeriesNav = useSeriesNav();
  const { visual } = useCommonGameSet();
  const [step, setStep] = useState<1 | 2>(1);

  const isStep2 = step === 2;
  const currentTextProps = isStep2 ? group2Props : group1Props;

  const shouldUseSetBackground =
    useSetBackground || (isSeriesNav && !!visual.welcomeBackDialog);
  const shouldHideBackground = hideBackground || shouldUseSetBackground;

  const [shine, setShine] = useState(false);
  return (
    <StyledRoot
      style={
        {
          '--rgb-color': currentTextProps.rgbColor || 'var(--c-rgb-green-400)',
          ...(shouldUseSetBackground ? visual.welcomeBackDialog : {}),
        } as unknown as CSSProperties
      }
      className={classNames({ hideBackground: shouldHideBackground })}
    >
      {header}
      <Vertical gap={3} center className="text-center">
        <Images style={{ gap: isStep2 ? 'calc(2 * var(--unit))' : '0' }}>
          <EntranceImageWrapper
            className={classNames({ animate: isStep2 })}
            onAnimationEnd={() => {
              setShine(true);
            }}
          >
            <RewardImageShine
              className={classNames({ visible: shine && !isStep2 })}
              style={
                {
                  '--mask-image': `url(${group1Props.imgUrls[0]})`,
                } as CSSProperties
              }
            />
            {group1Props.imgUrls.map(imgUrl => (
              <TransitionRewardImage key={imgUrl} src={imgUrl} alt="" />
            ))}
          </EntranceImageWrapper>
          <SecondImageWrapper
            style={{
              opacity: isStep2 ? 1 : 0,
              transform: isStep2 ? 'translateX(0)' : 'translateX(50%)',
            }}
          >
            {group2Props.imgUrls.map(imgUrl => (
              <TransitionRewardImage key={imgUrl} src={imgUrl} alt="" />
            ))}
          </SecondImageWrapper>
        </Images>
        <SetColorTheme disabled={!shouldUseSetBackground}>
          <TransitionTextArea>
            {!!currentTextProps.amount && (
              <BrandXXL color="var(--c-nd-600)">
                {currentTextProps.amount}
              </BrandXXL>
            )}
            <BrandL color="var(--c-white)">{currentTextProps.title}</BrandL>
            {!!currentTextProps.subtitle && (
              <LabelL color="var(--c-nd-600)">
                {currentTextProps.subtitle}
              </LabelL>
            )}
          </TransitionTextArea>
        </SetColorTheme>
      </Vertical>
      <ButtonsWrapper>
        <SetColorTheme disabled={!shouldUseSetBackground}>
          <div>{currentTextProps.disclaimer}</div>
        </SetColorTheme>
        {isStep2 ? (
          <>
            {group2Props.action}
            {!!showContinueButton && (
              <Button
                size="large"
                onClick={() => onClose?.()}
                color={continueButtonColor}
                disableDebounce
              >
                <FormattedMessage {...glossary.continue} />
              </Button>
            )}
          </>
        ) : (
          <Button size="large" color="secondary" onClick={() => setStep(2)}>
            <FormattedMessage {...messages.andMore} />
          </Button>
        )}
      </ButtonsWrapper>
    </StyledRoot>
  );
};
